import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Package, IndianRupee } from 'lucide-react';

import {
  Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, FactBand, Fact, CellButton, SidePane,
} from '@/components/data';

import { DEPT_NAMES, FINANCIAL_ROLES, canSeeCustomerMoney } from '@/lib/constants';
import { validateField, formatApiError } from '@/lib/validators';

import { STATUS_COLORS } from '@/lib/status-colors';

const PAYMENT_STATUS_COLORS = {
  unpaid: 'bg-red-50 text-red-700 border-red-200',
  partially_paid: 'bg-amber-50 text-amber-700 border-amber-200',
  fully_paid: 'bg-green-50 text-green-700 border-green-200',
};

export default function CustomerProfilePage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    order_id: 'none',
    amount: '',
    mode: 'cash',
    reference_number: '',
    remarks: '',
  });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  // Check if user can see financial data
  // the lifetime ledger is a narrower gate than recording one payment against
  // one order, which the city office does in the field
  const canSeeFinancials = canSeeCustomerMoney(user);
  const canRecordPayments = FINANCIAL_ROLES.includes(user?.role);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}/profile`);
      setProfile(res.data);
    } catch (err) {
      toast.error('Failed to load customer profile');
      navigate('/customers');
    }
    setLoading(false);
  }, [customerId, navigate]);

  const fetchOrders = useCallback(async () => {
    try {
      const params = { page: ordersPage, limit: 15 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (paymentStatusFilter && paymentStatusFilter !== 'all') params.payment_status = paymentStatusFilter;
      const res = await api.get(`/customers/${customerId}/orders-list`, { params });
      setOrders(res.data.orders);
      setOrdersTotal(res.data.total);
    } catch (err) {
    }
  }, [customerId, ordersPage, statusFilter, paymentStatusFilter]);

  const fetchPayments = useCallback(async () => {
    if (!canSeeFinancials) return;
    try {
      const res = await api.get(`/customers/${customerId}/payments-list`, { params: { page: paymentsPage, limit: 15 } });
      setPayments(res.data.payments);
      setPaymentsTotal(res.data.total);
    } catch (err) {
    }
  }, [customerId, paymentsPage, canSeeFinancials]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile) fetchOrders();
  }, [fetchOrders, profile]);

  // the payments rail is always on screen now, so it always loads
  useEffect(() => {
    if (profile) fetchPayments();
  }, [fetchPayments, profile]);

  const handleRecordPayment = async () => {
    const amountErr = validateField('amount', paymentForm.amount);
    if (amountErr) { toast.error(amountErr); return; }
    const modeErr = validateField('mode', paymentForm.mode);
    if (modeErr) { toast.error(modeErr); return; }
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setPaymentSubmitting(true);
    try {
      const payload = {
        amount: parseFloat(paymentForm.amount),
        mode: paymentForm.mode,
        reference_number: paymentForm.reference_number,
        remarks: paymentForm.remarks,
      };
      if (paymentForm.order_id && paymentForm.order_id !== 'none') payload.order_id = paymentForm.order_id;

      const res = await api.post(`/customers/${customerId}/payments`, payload);
      toast.success(res.data.message);
      setPaymentDialogOpen(false);
      setPaymentForm({ order_id: 'none', amount: '', mode: 'cash', reference_number: '', remarks: '' });
      fetchProfile();
      fetchOrders();
      fetchPayments();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to record payment'));
    }
    setPaymentSubmitting(false);
  };

  const openPaymentDialog = (orderId = 'none') => {
    setPaymentForm({ order_id: orderId, amount: '', mode: 'cash', reference_number: '', remarks: '' });
    setPaymentDialogOpen(true);
  };

  const formatDate = (dateStr) => formatIST(dateStr, { dateOnly: true });

  const formatDateTime = (dateStr) => formatIST(dateStr, { compact: true });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (!profile) return null;

  const { customer, financial_summary, outstanding_orders, aging_summary, recent_payments, customer_since, total_orders } = profile;
  const ordersPages = Math.ceil(ordersTotal / 15);
  const paymentsPages = Math.ceil(paymentsTotal / 15);

  const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

  const orderColumns = [
    {
      key: 'order_id',
      header: 'Order',
      width: '10rem',
      render: (o) => (
        <>
          <span className="block truncate font-semibold text-brand-blue">{o.order_id}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{formatDate(o.created_at)}</span>
        </>
      ),
    },
    {
      key: 'products',
      header: 'Items',
      width: 'minmax(12rem,1.5fr)',
      render: (o) => (
        <span className="flex flex-wrap gap-1">
          {(o.line_items || []).slice(0, 3).map((item, i) => (
            <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {DEPT_NAMES[item.order_type] || item.order_type}{item.quantity ? ` ×${item.quantity}` : ''}
            </span>
          ))}
          {(o.line_items?.length || 0) > 3 && (
            <span className="text-[10px] text-slate-400">+{o.line_items.length - 3}</span>
          )}
          {!o.line_items?.length && <span className="text-slate-400">—</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '8rem',
      render: (o) => (
        <Badge className={`text-[10px] ${STATUS_COLORS[o.status]?.class || ''}`}>
          {STATUS_COLORS[o.status]?.label || o.status}
        </Badge>
      ),
    },
    canSeeFinancials && {
      key: 'order_value',
      header: 'Value',
      align: 'right',
      width: '7.5rem',
      render: (o) => <span className="text-slate-700">{money(o.order_value)}</span>,
      total: (rows) => money(rows.reduce((s, o) => s + (o.order_value || 0), 0)),
    },
    canSeeFinancials && {
      key: 'total_paid',
      header: 'Paid',
      align: 'right',
      width: '7.5rem',
      render: (o) => <span className="text-emerald-600">{money(o.total_paid)}</span>,
      total: (rows) => money(rows.reduce((s, o) => s + (o.total_paid || 0), 0)),
    },
    canSeeFinancials && {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      width: '7.5rem',
      render: (o) => {
        const balance = (o.order_value || 0) - (o.total_paid || 0);
        return (
          <span className={`font-medium ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {money(balance)}
          </span>
        );
      },
      total: (rows) => money(rows.reduce((s, o) => s + ((o.order_value || 0) - (o.total_paid || 0)), 0)),
    },
    canSeeFinancials && {
      key: 'payment_status',
      header: 'Payment',
      width: '7rem',
      render: (o) => (
        <span className={`text-[11px] font-medium ${
          o.payment_status === 'fully_paid' ? 'text-emerald-600'
            : o.payment_status === 'partially_paid' ? 'text-amber-600' : 'text-red-600'
        }`}>
          {(o.payment_status || 'unpaid').replace('_', ' ')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '6rem',
      align: 'right',
      render: (o) => (
        <span className="flex items-center justify-end gap-0.5">
          {canRecordPayments && (o.order_value || 0) - (o.total_paid || 0) > 0 && (
            <CellButton icon={IndianRupee} label="Record payment" tone="good"
              onClick={() => openPaymentDialog(o.id)} />
          )}
          <CellButton icon={Package} label="Open order" iconOnly onClick={() => navigate(`/orders/${o.id}`)} />
        </span>
      ),
    },
  ].filter(Boolean);

  const aging = aging_summary || {};

  return (
    <Screen data-testid="customer-profile-page">
      <CommandBar
        title={customer.firm_name || customer.customer_name}
        subtitle={customer.customer_name !== customer.firm_name ? customer.customer_name : undefined}
        onBack={() => navigate('/customers')}
        actions={[
          canRecordPayments && {
            key: 'payment', label: 'Record Payment', icon: IndianRupee, tone: 'accent',
            onClick: () => openPaymentDialog('none'),
          },
          { key: 'new-order', label: 'New Order', icon: Package,
            onClick: () => navigate(`/orders/new?customer=${customerId}`) },
        ]}
      />

      <FactBand>
        <Fact label="Mobile">{customer.mobile_number}</Fact>
        <Fact label="Email">{customer.email}</Fact>
        <Fact label="GSTIN">{customer.gstin}</Fact>
        <Fact label="Customer since">{customer_since ? formatDate(customer_since) : '—'}</Fact>
        <Fact label="Billing">{customer.billing_address}</Fact>
        <Fact label="Delivery">{customer.delivery_address}</Fact>
        <Fact label="Orders">{total_orders || 0}</Fact>
        <Fact label="Open orders">{outstanding_orders?.length || 0}</Fact>
        {canSeeFinancials && (
          <>
            <Fact label="Billed">{money(financial_summary?.total_value)}</Fact>
            <Fact label="Paid">{money(financial_summary?.total_paid)}</Fact>
            <Fact label="Outstanding" valueClassName={(financial_summary?.outstanding || 0) > 0 ? 'font-medium text-red-600' : 'text-emerald-600'}>
              {money(financial_summary?.outstanding)}
            </Fact>
            <Fact label="Advance held">{money(financial_summary?.advance_balance)}</Fact>
          </>
        )}
      </FactBand>

      {canSeeFinancials && (
        <StatStrip
          stats={[
            { key: 'all', label: 'all orders', value: ordersTotal, tone: 'blue' },
            { key: 'unpaid', label: 'unpaid', value: aging.unpaid_count ?? '—', tone: 'danger' },
            { key: null, label: '0–30 days', value: money(aging['0_30'] ?? aging.days_0_30) },
            { key: null, label: '31–60', value: money(aging['31_60'] ?? aging.days_31_60), tone: 'warn' },
            { key: null, label: '60+', value: money(aging['60_plus'] ?? aging.days_60_plus), tone: 'danger' },
          ]}
          activeKey={paymentStatusFilter === 'all' ? 'all' : paymentStatusFilter}
          onSelect={(key) => {
            setPaymentStatusFilter(!key || key === 'all' ? 'all' : key);
            setOrdersPage(1);
          }}
        >
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setOrdersPage(1); }}>
            <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="Any status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Any status</SelectItem>
              {['design', 'in_production', 'ready_for_dispatch', 'dispatched', 'delivered'].map(s => (
                <SelectItem key={s} value={s} className="text-xs">
                  {STATUS_COLORS[s]?.label || s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </StatStrip>
      )}

      <ScreenSplit
        railWidth={canSeeFinancials ? '20rem' : '0rem'}
        rail={canSeeFinancials ? (
          <Pane title="Payments received" aside={`${paymentsTotal}`} className="max-h-full">
            {(payments.length ? payments : recent_payments || []).length === 0 ? (
              <p className="p-4 text-center text-xs text-slate-400">No payments recorded</p>
            ) : (payments.length ? payments : recent_payments).map((p, i) => (
              <div key={p.id || i} className="flex items-start justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
                <span className="min-w-0">
                  <span className="block truncate text-[12px] font-medium text-slate-700">
                    {p.order_display_id || p.order_id || 'On account'}
                  </span>
                  <span className="block truncate text-[10px] text-slate-400">
                    {p.mode}{p.reference_number ? ` · ${p.reference_number}` : ''}
                  </span>
                  <span className="block text-[10px] text-slate-400">
                    {p.date ? formatDateTime(p.date) : '—'}
                    {p.recorded_by_name ? ` · ${p.recorded_by_name}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] font-bold tabular-nums text-emerald-600">
                  {money(p.amount)}
                </span>
              </div>
            ))}
            {paymentsPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-2.5 py-1.5 text-[11px] text-slate-500">
                <button type="button" disabled={paymentsPage <= 1}
                  onClick={() => setPaymentsPage(p => p - 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">Prev</button>
                <span>{paymentsPage} / {paymentsPages}</span>
                <button type="button" disabled={paymentsPage >= paymentsPages}
                  onClick={() => setPaymentsPage(p => p + 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">Next</button>
              </div>
            )}
          </Pane>
        ) : null}
      >
        <Pane
          title="Order ledger"
          aside={`${orders.length} of ${ordersTotal}`}
          scroll={false}
          bodyClassName="flex flex-col"
          actions={ordersPages > 1 && (
            <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
              <button type="button" disabled={ordersPage <= 1} onClick={() => setOrdersPage(p => p - 1)}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
              {ordersPage} / {ordersPages}
              <button type="button" disabled={ordersPage >= ordersPages} onClick={() => setOrdersPage(p => p + 1)}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
            </span>
          )}
        >
          <DataGrid
            data-testid="customer-orders-table"
            columns={orderColumns}
            rows={orders}
            getRowId={(o) => o.id}
            onRowClick={(o) => navigate(`/orders/${o.id}`)}
            showTotals={canSeeFinancials && orders.length > 0}
            empty="No orders for this customer yet"
          />
        </Pane>
      </ScreenSplit>

      <SidePane
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        title="Record payment"
        subtitle={customer.customer_name}
        width="24rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleRecordPayment} disabled={paymentSubmitting}
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {paymentSubmitting ? 'Saving…' : 'Record payment'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Against order</span>
            <Select value={paymentForm.order_id} onValueChange={v => setPaymentForm(p => ({ ...p, order_id: v }))}>
              <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs">On account (no specific order)</SelectItem>
                {(outstanding_orders || []).map(o => (
                  <SelectItem key={o.id} value={o.id} className="text-xs">
                    {o.order_id} — {money((o.order_value || 0) - (o.total_paid || 0))} due
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Amount *</span>
              <Input type="number" className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={paymentForm.amount}
                onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Mode *</span>
              <Select value={paymentForm.mode} onValueChange={v => setPaymentForm(p => ({ ...p, mode: v }))}>
                <SelectTrigger className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['cash', 'upi', 'bank_transfer', 'cheque', 'card'].map(m => (
                    <SelectItem key={m} value={m} className="text-xs">{m.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Reference</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="UTR, cheque number…"
              value={paymentForm.reference_number}
              onChange={e => setPaymentForm(p => ({ ...p, reference_number: e.target.value }))} />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Remarks</span>
            <Textarea className="mt-0.5 resize-none text-sm" rows={2} value={paymentForm.remarks}
              onChange={e => setPaymentForm(p => ({ ...p, remarks: e.target.value }))} />
          </label>
        </div>
      </SidePane>
    </Screen>
  );
}
